import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, ResearchCost } from '../game/types';

export class ResourceSystem {
  constructor(
    private gameState: GameStateManager,
    private gameData: GameData
  ) {}

  updateBudgetDrain(): void {
    const state = this.gameState.getState();
    const config = this.gameData.gameConfig;
    let baseDrain = config.budget_drain_base;
    
    // Apply extraBudjetLossPerTick effect (Phantom Researcher)
    const extraBudgetLoss = this.gameState.getEffectValue('extraBudjetLossPerTick');
    if (typeof extraBudgetLoss === 'number') {
      baseDrain += extraBudgetLoss;
    }
    
    const drain = baseDrain + (state.researchers.length * config.budget_drain_per_researcher);
    
    // Check if budget would go below minimum threshold
    const currentBudget = this.gameState.getBudget();
    const budgetMinimum = this.gameData.gameEndData.budget_minimum;
    
    if (currentBudget - drain < budgetMinimum) {
      // Trigger defeat - budget depleted
      this.gameState.setGameEndState('defeat', 'budget');
      return;
    }
    
    this.gameState.addBudget(-drain);
  }

  updateOngoingActions(researcher: Researcher): { shouldCancel: boolean; shouldComplete: boolean } {
    const costs = this.gameData.costs;
    let shouldCancel = false;
    let shouldComplete = false;

    if (researcher.currentAction === 'Synthetize') {
      // Check if chemicals are already at max before processing
      const chemicals = this.gameState.getChemicals();
      const maxChemicals = this.gameState.getState().maxChemicals;
      
      if (chemicals >= maxChemicals) {
        // Mark as complete when at max capacity (not cancel - it finished successfully)
        shouldComplete = true;
      } else {
        // Generate 1 chemical per synthetize_rate_ticks
        if (researcher.actionTimer > 0 && researcher.actionTimer % costs.synthetize_rate_ticks === 0) {
          this.gameState.addChemicals(1);
          
          // Check again after adding - if we hit max, mark as complete
          const newChemicals = this.gameState.getChemicals();
          if (newChemicals >= maxChemicals) {
            shouldComplete = true;
          }
        }
      }
    } else if (researcher.currentAction === 'Rest') {
      // Regenerate 1 sanity per rest_rate_ticks
      if (researcher.actionTimer > 0 && researcher.actionTimer % costs.rest_rate_ticks === 0) {
        const maxSanity = researcher.data.sanity;
        if (researcher.currentSanity < maxSanity) {
          researcher.currentSanity = Math.min(maxSanity, researcher.currentSanity + 1);
        }
      }
      
      // Check if sanity is at max - complete if no insane researchers present
      const maxSanity = researcher.data.sanity;
      if (researcher.currentSanity >= maxSanity) {
        // Only auto-complete if there are no insane researchers (sanity < 50% of max)
        const hasInsaneResearchers = this.hasInsaneResearchers(researcher.id);
        if (!hasInsaneResearchers) {
          shouldComplete = true;
        }
      }
    }

    return { shouldCancel, shouldComplete };
  }

  canAffordCosts(costs: ResearchCost[]): { canAfford: boolean; missing: string[] } {
    const missing: string[] = [];
    
    for (const cost of costs) {
      if (cost.budget !== undefined) {
        if (this.gameState.getBudget() < cost.budget) {
          missing.push('budget');
        }
      }
      if (cost.chemicals !== undefined) {
        if (this.gameState.getChemicals() < cost.chemicals) {
          missing.push('chemicals');
        }
      }
      if (cost.computation !== undefined) {
        if (this.gameState.getComputingAvailable() < cost.computation) {
          missing.push('computation');
        }
      }
      if (cost.silver_bullets !== undefined) {
        const researcher = this.gameState.getState().researchers.find(r => 
          r.currentAction === 'Research' && r.silverBullet !== null
        );
        if (!researcher || !researcher.silverBullet) {
          missing.push('silver_bullets');
        }
      }
      if (cost.unlock_warp !== undefined && cost.unlock_warp) {
        if (!this.gameState.isWarpUnlocked()) {
          missing.push('warp_unlocked');
        }
      }
    }

    return {
      canAfford: missing.length === 0,
      missing
    };
  }

  spendCosts(costs: ResearchCost[]): boolean {
    const check = this.canAffordCosts(costs);
    if (!check.canAfford) {
      return false;
    }

    for (const cost of costs) {
      if (cost.budget !== undefined) {
        if (!this.gameState.spendBudget(cost.budget)) {
          return false;
        }
      }
      if (cost.chemicals !== undefined) {
        if (!this.gameState.spendChemicals(cost.chemicals)) {
          return false;
        }
      }
      if (cost.computation !== undefined) {
        if (!this.gameState.reserveComputing(cost.computation)) {
          return false;
        }
      }
    }

    return true;
  }

  calculateTotalCosts(costs: ResearchCost[]): { budget: number; chemicals: number; computation: number } {
    let budget = 0;
    let chemicals = 0;
    let computation = 0;

    for (const cost of costs) {
      if (cost.budget !== undefined) budget += cost.budget;
      if (cost.chemicals !== undefined) chemicals += cost.chemicals;
      if (cost.computation !== undefined) computation += cost.computation;
    }

    return { budget, chemicals, computation };
  }

  private hasInsaneResearchers(excludeResearcherId?: string): boolean {
    // Check if there are any researchers with sanity below the insane threshold
    const researchers = this.gameState.getResearchers();
    const insaneThreshold = this.gameData.insanityConfig.insane_threshold;
    for (const r of researchers) {
      if (excludeResearcherId && r.id === excludeResearcherId) {
        continue; // Don't check the researcher who is resting
      }
      const maxSanity = r.data.sanity;
      const relativeSanity = r.currentSanity / maxSanity;
      if (relativeSanity < insaneThreshold) {
        return true; // Found an insane researcher
      }
    }
    return false;
  }
}


